import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, FileText, TrendingUp, Download, ArrowRight, X, Mail, User, Loader2 } from 'lucide-react';
import { useSound } from '../hooks/useSound';
import { useNavigate } from 'react-router-dom';

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

const categories = [
  {
    title: 'Strategic Growth Guides',
    icon: BookOpen,
    resources: [
      {
        title: 'Service Industry 2.0',
        description: 'How AI is Redefining Client Management and Efficiency',
        content: {
          title: 'Service Industry 2.0: How AI is Redefining Client Management and Efficiency',
          sections: [
            {
              title: 'The Shift in the Service Industry',
              content: `The service industry is undergoing a transformation. Customer expectations are evolving, and businesses that rely on outdated methods risk falling behind. Clients now demand faster response times, personalized experiences, and seamless interactions, all while businesses struggle with operational bottlenecks, workforce management, and pricing pressures.

For service-based businesses, success no longer depends solely on expertise—it requires efficiency, agility, and the ability to anticipate client needs before they arise.`
            },
            {
              title: 'The Challenges Facing Service-Based Businesses',
              content: [
                'Time-Intensive Manual Processes – Administrative tasks slow down response times and reduce overall efficiency.',
                'Inconsistent Customer Retention – Businesses struggle to identify when and why clients disengage.',
                'Pricing and Profitability Pressures – Many service providers underprice their offerings or fail to maximize revenue opportunities.',
                'Lack of Performance Tracking – Without clear insights into productivity and profitability, businesses struggle to optimize operations.'
              ]
            },
            {
              title: 'How AI is Revolutionizing the Service Industry',
              content: [
                'Automated Client Engagement – AI-powered scheduling, follow-ups, and customer relationship tracking ensure seamless interactions.',
                'Predictive Customer Retention Insights – Businesses can identify disengaged clients and take proactive steps to keep them engaged.',
                'Smart Pricing Optimization – AI analyzes demand, competition, and client behaviors to recommend more profitable pricing structures.',
                'Operational Efficiency Enhancements – AI reduces administrative burdens by automating routine tasks and workforce scheduling.'
              ]
            },
            {
              title: 'How ProfitIQ Supports Service-Based Businesses',
              content: `Service businesses that implement AI-driven insights stay ahead of customer expectations while maintaining operational efficiency and profitability. ProfitIQ enables service providers to track financial performance, monitor client retention metrics, and optimize pricing strategies—all in real time.

Success in the service industry is no longer about simply delivering a great service. Businesses that anticipate client needs, optimize workflows, and leverage AI-driven decision-making will dominate their industries.`
            }
          ]
        }
      },
      {
        title: 'AI-Powered Decision Making',
        description: 'How SMBs Can Remove Guesswork and Improve Strategy',
        content: {
          title: 'AI-Powered Decision Making: How SMBs Can Remove Guesswork and Improve Strategy',
          sections: [
            {
              title: 'The Challenge of Decision-Making in SMBs',
              content: `Running a business requires constant decision-making, but many SMBs operate without real-time data to guide those decisions. Business owners often rely on gut instincts, outdated reports, or trial and error, leading to inefficient resource allocation and missed opportunities.

Without a structured decision-making process, businesses risk financial instability, ineffective growth strategies, and inefficient operations.`
            },
            {
              title: 'The Most Common Decision-Making Pitfalls',
              content: [
                'Reactive Rather Than Proactive Strategies – Businesses respond to problems instead of anticipating them.',
                'Lack of Financial Visibility – Poor insight into revenue trends, expenses, and profitability metrics.',
                'Inconsistent Market Positioning – No clear understanding of competitive standing or pricing effectiveness.',
                'Misaligned Resource Allocation – Investments are made without data-backed justifications.'
              ]
            },
            {
              title: 'The Role of AI in Smarter Decision-Making',
              content: [
                'Real-Time Business Insights – Instant visibility into financial performance and operational efficiency.',
                'Predictive Business Modeling – Scenario planning for different business strategies.',
                'Competitive Benchmarking – Clear comparisons to industry leaders and market trends.',
                'Automated Performance Tracking – Continuous monitoring of key metrics to adjust strategies dynamically.'
              ]
            },
            {
              title: 'How ProfitIQ Helps SMBs Make Smarter Decisions',
              content: `Business success is built on informed decision-making. ProfitIQ provides business owners with the tools to analyze financial health, track industry trends, and adjust strategies based on real-time insights.

With AI-powered business intelligence, SMBs can shift from reactive decision-making to a proactive approach—ensuring smarter investments, optimized pricing, and sustainable growth.`
            }
          ]
        }
      },
      {
        title: 'Scaling with AI',
        description: 'Automating Operations for Sustainable Business Growth',
        content: {
          title: 'Scaling with AI: Automating Operations for Sustainable Business Growth',
          sections: [
            {
              title: 'The Growing Pains of Scaling an SMB',
              content: `Scaling a business presents unique challenges. Many SMBs struggle with inefficiencies that limit their ability to grow without adding excessive costs. Without the right systems in place, rapid growth can lead to operational breakdowns, increased expenses, and resource mismanagement.

For businesses looking to expand, scalability requires more than just increasing sales—it demands automation, workflow optimization, and streamlined operations.`
            },
            {
              title: 'The Biggest Scaling Challenges SMBs Face',
              content: [
                'Manual and Repetitive Processes – Administrative tasks increase as the business grows.',
                'Limited Visibility into Financial Health – Growth often masks cash flow challenges.',
                'Operational Bottlenecks – Inconsistent workflows slow down productivity and service delivery.',
                'Customer Retention Struggles – Growth efforts focus on acquiring new clients rather than maintaining existing ones.'
              ]
            },
            {
              title: 'How AI Supports Scalable Business Growth',
              content: [
                'Streamlining Administrative Workflows – Automating invoicing, scheduling, and reporting.',
                'Optimizing Financial Management – AI-powered forecasting ensures stable cash flow.',
                'Enhancing Workforce Productivity – AI-driven analytics provide insights into team performance and efficiency.',
                'Improving Customer Engagement – Automated follow-ups and personalized experiences increase retention.'
              ]
            },
            {
              title: 'How ProfitIQ Helps SMBs Scale',
              content: `Sustainable scaling requires a clear understanding of financials, operations, and customer retention strategies. ProfitIQ provides SMBs with real-time insights that make scaling seamless—ensuring business growth without unnecessary complexity or inefficiencies.

Businesses that integrate AI-powered automation into their scaling efforts position themselves for long-term success.`
            }
          ]
        }
      }
    ]
  },
  {
    title: 'Industry Trend Reports',
    icon: TrendingUp,
    resources: [
      {
        title: 'Manufacturing Optimization Trends',
        description: 'Latest trends in manufacturing process optimization.',
        content: {
          title: 'Manufacturing Optimization Trends: AI-Driven Process Improvement',
          sections: [
            {
              title: 'The Evolution of Manufacturing',
              content: `The manufacturing sector is experiencing a profound transformation driven by AI and automation. Traditional manufacturing processes are being reimagined through the lens of data analytics, machine learning, and predictive maintenance. This shift is creating new opportunities for efficiency, quality, and cost reduction.

Companies that embrace these changes are seeing significant improvements in their operational metrics, while those that delay risk falling behind in an increasingly competitive market.`
            },
            {
              title: 'Key Optimization Trends',
              content: [
                'Predictive Maintenance – AI-powered systems predict equipment failures before they occur',
                'Smart Resource Allocation – Real-time optimization of workforce and material resources',
                'Quality Control Automation – Computer vision and AI for defect detection',
                'Supply Chain Intelligence – Predictive analytics for supply chain optimization'
              ]
            },
            {
              title: 'Implementation Challenges',
              content: [
                'Integration with Legacy Systems – Bridging the gap between old and new technologies',
                'Workforce Adaptation – Training and upskilling existing employees',
                'Data Quality and Collection – Ensuring accurate and comprehensive data gathering',
                'ROI Justification – Demonstrating clear value from optimization investments'
              ]
            },
            {
              title: 'Future Outlook',
              content: `The future of manufacturing optimization lies in the convergence of AI, IoT, and advanced analytics. Successful manufacturers will be those who can effectively combine these technologies to create smart, adaptive production environments.

ProfitIQ.AI helps manufacturers navigate this transformation by providing clear implementation strategies and measurable optimization targets.`
            }
          ]
        }
      },
      {
        title: 'Retail Market Evolution',
        description: 'How AI is transforming the retail landscape.',
        content: {
          title: 'Retail Market Evolution: AI-Powered Customer Experience',
          sections: [
            {
              title: 'The New Retail Landscape',
              content: `The retail industry is undergoing a fundamental shift as AI technologies reshape customer expectations and business operations. Traditional retail models are being challenged by data-driven approaches that prioritize personalization, convenience, and seamless omnichannel experiences.

Success in modern retail requires a deep understanding of customer behavior and the ability to adapt quickly to changing market conditions.`
            },
            {
              title: 'Emerging Retail Trends',
              content: [
                'Personalized Shopping Experiences – AI-driven product recommendations and customer journey optimization',
                'Inventory Intelligence – Smart inventory management and demand forecasting',
                'Omnichannel Integration – Seamless integration of online and offline channels',
                'Dynamic Pricing Strategies – Real-time price optimization based on market conditions'
              ]
            },
            {
              title: 'Customer Experience Innovation',
              content: [
                'AI-Powered Customer Service – Intelligent chatbots and support systems',
                'Smart Store Technologies – IoT-enabled shopping experiences',
                'Loyalty Program Enhancement – Data-driven customer retention strategies',
                'Mobile-First Engagement – Advanced mobile shopping and engagement tools'
              ]
            },
            {
              title: 'ProfitIQ AI Retail Solutions',
              content: `We help retailers navigate this evolution by providing AI-powered tools for customer analysis, inventory optimization, and business intelligence. Our solutions enable retailers to make data-driven decisions that improve customer satisfaction and drive revenue growth.

The future of retail belongs to businesses that can effectively leverage AI to create exceptional customer experiences while maintaining operational efficiency.`
            }
          ]
        }
      },
      {
        title: 'Service Industry Innovation',
        description: 'Innovation trends in professional services.',
        content: {
          title: 'Service Industry Innovation: The Future of Professional Services',
          sections: [
            {
              title: 'Transforming Service Delivery',
              content: `The professional services industry is being revolutionized by AI and automation technologies. Traditional service models are evolving to incorporate data-driven insights, automated workflows, and enhanced client experiences.

Firms that embrace innovation are finding new ways to deliver value while improving efficiency and scalability.`
            },
            {
              title: 'Innovation Drivers',
              content: [
                'Client Experience Enhancement – AI-powered service personalization and delivery',
                'Workflow Automation – Streamlined processes and reduced manual tasks',
                'Knowledge Management – Intelligent systems for expertise sharing',
                'Remote Service Delivery – Advanced tools for virtual client engagement'
              ]
            },
            {
              title: 'Implementation Strategies',
              content: [
                'Digital Transformation – Modernizing service delivery platforms',
                'Data Analytics Integration – Leveraging client data for better insights',
                'Team Collaboration Tools – Enhanced internal communication systems',
                'Client Portal Development – Self-service and engagement platforms'
              ]
            },
            {
              title: 'Future of Professional Services',
              content: `The next generation of professional services will be characterized by intelligent automation, data-driven decision making, and enhanced client collaboration. ProfitIQ.AI helps service firms adapt to these changes by providing tools and strategies for successful digital transformation.

Success in this evolving landscape requires a balance of traditional expertise and innovative technology solutions.`
            }
          ]
        }
      }
    ]
  },
  {
    title: 'Leadership Perspectives',
    icon: FileText,
    resources: [
      {
        title: 'Strategic Decision-Making in SMBs',
        description: 'Expert insights on data-driven decision making.',
        content: {
          title: 'Strategic Decision-Making in SMBs: A Data-Driven Approach',
          sections: [
            {
              title: 'The Evolution of Business Decision-Making',
              content: `Modern business leadership requires a sophisticated approach to decision-making that combines data analytics with strategic thinking. Traditional intuition-based decisions are being replaced by data-driven strategies that provide clearer insights and more predictable outcomes.

SMB leaders must adapt to this new paradigm while maintaining the agility that makes small businesses competitive.`
            },
            {
              title: 'Key Decision-Making Challenges',
              content: [
                'Data Overload – Managing and interpreting large amounts of business data',
                'Market Uncertainty – Navigating rapidly changing market conditions',
                'Resource Allocation – Optimizing limited resources for maximum impact',
                'Competitive Pressure – Maintaining advantage in competitive markets'
              ]
            },
            {
              title: 'Data-Driven Strategy Framework',
              content: [
                'Performance Analytics – Comprehensive business performance tracking',
                'Market Intelligence – Real-time market trend analysis',
                'Predictive Modeling – Future scenario planning and risk assessment',
                'ROI Optimization – Investment return analysis and optimization'
              ]
            },
            {
              title: 'Implementation Guide',
              content: `Successful implementation of data-driven decision-making requires a structured approach that includes:
              
1. Establishing clear metrics and KPIs
2. Developing data collection and analysis processes
3. Creating feedback loops for continuous improvement
4. Building a culture of data-driven decision making

ProfitIQ.AI provides the tools and guidance needed to transform your decision-making process.`
            }
          ]
        }
      },
      {
        title: 'Scaling Operations with AI',
        description: 'Practical guide to AI implementation in business.',
        content: {
          title: 'Scaling Operations with AI: A Practical Implementation Guide',
          sections: [
            {
              title: 'The AI Scaling Advantage',
              content: `Artificial Intelligence offers unprecedented opportunities for businesses to scale operations efficiently. By automating routine tasks and providing advanced analytics, AI enables companies to grow without proportional increases in operational costs.

The key to successful scaling lies in strategic AI implementation that addresses specific business needs while maintaining quality and consistency.`
            },
            {
              title: 'Core Scaling Challenges',
              content: [
                'Process Standardization – Creating scalable, repeatable processes',
                'Quality Maintenance – Maintaining service quality during growth',
                'Resource Management – Efficient allocation of human and material resources',
                'Technology Integration – Seamless integration of AI tools'
              ]
            },
            {
              title: 'AI Implementation Strategy',
              content: [
                'Process Analysis – Identifying automation opportunities',
                'Technology Selection – Choosing appropriate AI solutions',
                'Change Management – Managing organizational transformation',
                'Performance Monitoring – Tracking and optimizing AI systems'
              ]
            },
            {
              title: 'Measuring Success',
              content: `Effective AI scaling requires clear metrics for success:

1. Operational efficiency improvements
2. Cost reduction measurements
3. Quality control metrics
4. Customer satisfaction indicators

ProfitIQ.AI helps businesses implement and optimize AI solutions for sustainable growth.`
            }
          ]
        }
      },
      {
        title: 'Building High-Performance Teams',
        description: 'Strategies for team optimization and growth.',
        content: {
          title: 'Building High-Performance Teams: AI-Enhanced Talent Management',
          sections: [
            {
              title: 'The Modern Team Dynamic',
              content: `High-performance teams in today's business environment require a combination of traditional leadership principles and modern technology-enabled practices. AI tools are transforming how we build, manage, and optimize team performance.

Success depends on creating an environment where technology enhances rather than replaces human capabilities.`
            },
            {
              title: 'Team Building Fundamentals',
              content: [
                'Talent Acquisition – AI-powered recruitment and selection',
                'Skill Development – Personalized training and development programs',
                'Performance Optimization – Data-driven performance management',
                'Team Cohesion – Building strong team dynamics'
              ]
            },
            {
              title: 'Performance Enhancement Strategies',
              content: [
                'Goal Alignment – Ensuring team and organizational goal alignment',
                'Communication Tools – Implementing effective communication systems',
                'Productivity Tracking – Measuring and optimizing team productivity',
                'Recognition Systems – Creating effective reward and recognition programs'
              ]
            },
            {
              title: 'Sustainable Team Excellence',
              content: `Building and maintaining high-performance teams requires ongoing attention to:

1. Regular performance assessment and feedback
2. Continuous skill development and training
3. Team culture and engagement
4. Work-life balance and well-being

ProfitIQ.AI provides tools and strategies for developing and maintaining high-performance teams.`
            }
          ]
        }
      }
    ]
  }
];

export const Resources = () => {
  const { playHover, playClick } = useSound();
  const [selectedGuide, setSelectedGuide] = useState<any>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const openGuide = (guide: any) => {
    setSelectedGuide(guide);
    setShowLeadForm(true);
    playClick();
  };

  const closeGuide = () => {
    setSelectedGuide(null);
    setShowLeadForm(false);
    setFormData({ name: '', email: '' });
    setError(null);
    setSuccess(false);
    playClick();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    playClick();

    try {
      // Validate form
      if (!formData.name.trim() || !formData.email.trim()) {
        throw new Error('Please fill in all fields');
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      setShowLeadForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 px-4 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ...springConfig }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              Resources & Insights
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Expert guidance on business growth, strategy, and operations
          </p>
        </motion.div>
        
        <div className="space-y-16">
          {categories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.2, duration: 1, ...springConfig }}
            >
              <div className="mb-8 flex items-center gap-3">
                <category.icon className="w-8 h-8 text-amber-400" />
                <h2 className="text-2xl font-bold text-white">{category.title}</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {category.resources.map((resource, index) => (
                  <motion.div
                    key={resource.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (categoryIndex * 0.2) + (index * 0.1), duration: 1, ...springConfig }}
                  >
                    <motion.div
                      whileHover={{ y: -5, scale: 1.02 }}
                      transition={{ duration: 0.8, ...springConfig }}
                      className="h-full p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm group"
                    >
                      <h3 className="text-xl font-semibold text-white mb-3">{resource.title}</h3>
                      <p className="text-gray-400 mb-6">{resource.description}</p>
                      <div className="flex gap-4">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          transition={springConfig}
                          onMouseEnter={playHover}
                          onClick={() => openGuide(resource)}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-sm hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300"
                        >
                          <Download className="w-4 h-4" />
                          View Guide
                        </motion.button>
                        <motion.button
                          whileHover={{ x: 4 }}
                          whileTap={{ scale: 0.98 }}
                          transition={springConfig}
                          onMouseEnter={playHover}
                          onClick={() => openGuide(resource)}
                          className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors duration-300"
                        >
                          Learn More
                          <ArrowRight className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Guide Modal */}
        {selectedGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/80"
            onClick={closeGuide}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-background rounded-xl p-8"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={closeGuide}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {showLeadForm ? (
                <div className="max-w-md mx-auto">
                  <h2 className="text-2xl font-bold mb-4 text-center">
                    <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                      Get Instant Access
                    </span>
                  </h2>
                  <p className="text-gray-400 mb-6 text-center">
                    Enter your details to access the complete guide
                  </p>

                  {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-center">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                        Full Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
                        <input
                          type="text"
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
                        <input
                          type="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springConfig}
                      className="w-full px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Access Guide'
                      )}
                    </motion.button>
                  </form>
                </div>
              ) : (
                <>
                  <h2 className="text-3xl font-bold mb-6">
                    <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                      {selectedGuide.content.title}
                    </span>
                  </h2>

                  <div className="space-y-8">
                    {selectedGuide.content.sections.map((section: any, index: number) => (
                      <motion.div
                        key={section.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.5 }}
                      >
                        <h3 className="text-xl font-semibold text-white mb-4">{section.title}</h3>
                        {Array.isArray(section.content) ? (
                          <ul className="space-y-2">
                            {section.content.map((item: string, i: number) => (
                              <li key={i} className="flex items-start gap-2 text-gray-300">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-300 whitespace-pre-line">{section.content}</p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};